import React, { useEffect, useState } from "react";
import { useFloating, autoUpdate, useDismiss, useInteractions, offset, size } from "@floating-ui/react";
import useSWRInfinite from "swr/infinite";
import useSWRImmutable from "swr/immutable";
import { getIconsByQuery, getIconsForEmptyState } from "frontend/api/noun";
import style from "./noun-icon-submenu.module.css";
import { SearchField } from "frontend/ui-components/search-field/search-field";
import { CloseButton } from "frontend/ui-components/dialog-close-button/dialog-close-button";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useDebounce } from "react-use";
import { SimpleSpinner } from "frontend/ui-components/loaders/loaders";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import StyledButton from "frontend/ui-components/styled-button";
import classNames from "classnames";
import { isDraggingElementAtom, sidePanelType, SidepanelType } from "state-atoms";
import { getPathPrefix } from "utils/getPathPrefix";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { Box } from "frontend/ui-components/design-system/box/box";
import { nounIconCIM } from "../sidebar/side-menu-items/side-menu-icons";

interface NounIconCreatorSchema {
  name: string;
  permalink: string;
  username: string;
}

interface NounIconData {
  attribution: string;
  id: string;
  icon_url?: string;
  license_description: string;
  permalink: string;
  term: string;
  thumbnail_url: string;
  collections: Array<{
    id: string;
    name: string;
    permalink?: string;
    creator: NounIconCreatorSchema;
  }>;
  tags: string[];
  creator: NounIconCreatorSchema;
}

interface NounIconResults {
  icons: Array<NounIconData>;
  next_page?: string;
  prev_page?: string;
  total: number;
  generated_at: string;
}

export const nounIconPanelState = atom(false);

export const NounIconPanelContainer = React.memo(
  ({
    onChooseIcon,
    onDismiss,
  }: {
    onChooseIcon: (icon: { id: string; thumbnail: string; icon_url?: string }) => void;
    onDismiss: () => void;
  }) => {
    const [nounOpen, setNounOpen] = useAtom(nounIconPanelState);
    const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);
    const isCimReorderEnabled = useIsCIMReorderEnabled();
    const open = isCimReorderEnabled ? openedSidePanel === SidepanelType.nounIcon : nounOpen;

    return open ? (
      <NounIconPanel
        onChooseIcon={onChooseIcon}
        onDismiss={() => {
          if (isCimReorderEnabled) {
            setOpenedSidePanel(SidepanelType.none);
          } else {
            setNounOpen(false);
          }
          onDismiss();
        }}
      />
    ) : null;
  }
);
NounIconPanelContainer.displayName = "NounIconPanel";

function NounIconPanel({
  onChooseIcon,
  onDismiss,
}: {
  onChooseIcon: (value: { id: string; thumbnail: string; icon_url?: string }) => void;
  onDismiss: (open: boolean) => void;
}) {
  const referenceObject = document.getElementById("tools-sidemenu");
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const { refs, floatingStyles, context } = useFloating({
    elements: { reference: referenceObject },
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    open: true,
    onOpenChange: onDismiss,
    middleware: [
      offset(({ rects }) => {
        const sidemenu = document.getElementById("tools-sidemenu");
        const y = sidemenu?.getBoundingClientRect().y || 90;
        return {
          mainAxis: 10, // if I ever want to add arrow, 14 is arrow width and should be added to offset
          crossAxis: -rects.reference.y + y,
        };
      }),
      size({
        apply({ availableWidth, elements }) {
          const sidemenu = document.getElementById("tools-sidemenu");
          const height = sidemenu?.getBoundingClientRect().height || window.innerHeight - 180;
          elements.floating.style.height = `${height}px`;
          elements.floating.style.maxWidth = `${availableWidth}px`;
        },
      }),
    ],
  });
  const dismiss = useDismiss(context, { outsidePress: false });
  const { getFloatingProps } = useInteractions([dismiss]);

  // --------------------------------
  const query = useAtomValue(debouncedSearchQuery);

  const getKey = (pageIndex: number, previousPageData: NounIconResults) => {
    if (previousPageData && !previousPageData.next_page) return null;
    let trimmedQuery = query.trim();
    if (!trimmedQuery) return null;
    if (pageIndex == 0) return ["noun-icons", trimmedQuery];
    return ["noun-icons", trimmedQuery, previousPageData.next_page];
  };

  const {
    data,
    isLoading,
    error,
    setSize,
    size: resultSize,
    mutate,
  } = useSWRInfinite<NounIconResults>(getKey, getIconsByQuery, { revalidateAll: true });
  const { data: defaultIcons } = useSWRImmutable("defaults", getIconsForEmptyState);

  let startIcons: Array<NounIconData> = [];
  if (defaultIcons) {
    const icon = defaultIcons?.icon?.icons ?? [];
    const business = defaultIcons?.business?.icons ?? [];
    startIcons = [...icon, ...business];
  }

  // --------------------------------
  const [selectedIconId, setSelectedIconId] = useState<string | undefined>();
  const [openInfoPanel, setOpenInfoPanel] = useState(false);
  // const { data: autocomplete } = useSWR(searchQuery || null, getIconsSuggestions);
  //autocomplete.suggestions: string[]

  // TODO: useSWR docs have example on how to load the next page and keep it hidden,
  // even before the user requests it.

  const iconsToDisplay = query ? (data ? data.flatMap(({ icons }) => icons) : []) : startIcons;
  const selectedIcon = selectedIconId ? iconsToDisplay.find(({ id }) => id == selectedIconId) : undefined;
  const totalIconsForQuery = data ? data[0].total : -1;

  const Content = (
    <>
      {isCimReorderEnabled ? (
        <div className={style.closeIcon} onClick={() => onDismiss(false)}>
          <CloseIcon color="#848199" />
        </div>
      ) : (
        <CloseButton ariaLabel="Close" onClick={() => onDismiss(false)} />
      )}

      {openInfoPanel && selectedIcon ? (
        <IconInfoPanel
          icon={selectedIcon}
          onChooseIcon={() => {
            tracking.trackAnalyticsEvent("noun-icon-info-view-click", { id: selectedIcon.id });
            onChooseIcon({
              id: selectedIcon.id,
              thumbnail: selectedIcon.thumbnail_url,
              icon_url: (selectedIcon as any).icon_url,
            });
          }}
          onBack={() => setOpenInfoPanel(false)}
        />
      ) : (
        <IconSearchPanel
          data={iconsToDisplay}
          isCIM={isCimReorderEnabled}
          error={error}
          selected={selectedIconId}
          onLoadMore={() => setSize((size) => size + 1)}
          totalIconsForQuery={totalIconsForQuery}
          isLoading={(!error && isLoading) || (!!data && data.length < resultSize)}
          onIconInfoClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setSelectedIconId(e.currentTarget.dataset.id);
            setOpenInfoPanel(true);
          }}
          onIconClick={(e) => {
            const id = e.currentTarget.dataset.id;
            if (id) {
              setSelectedIconId(id);
              const icon = iconsToDisplay.find((icon) => icon.id == id);
              if (icon) {
                tracking.trackAnalyticsEvent("noun-icon-results-view-click", { id: icon.id });
                onChooseIcon({
                  id,
                  thumbnail: (e.currentTarget.children[0] as HTMLImageElement).src,
                  icon_url: (icon as any)?.icon_url,
                });
              }
            } else {
              console.error("Required field (id) missing from icon button");
            }
          }}
          onTryAfterError={() => {
            mutate(data, { revalidate: true });
          }}
        />
      )}
    </>
  );

  if (isCimReorderEnabled) {
    return (
      <Box variant="modal" className={style.cimLayout}>
        {Content}
      </Box>
    );
  }

  return (
    <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
      <div className={style.layout} style={{ height: "100%", width: "350px" }}>
        {Content}
      </div>
    </div>
  );
}

function Title({ isCIM }: { isCIM: boolean }) {
  return (
    <div data-cim={isCIM} className={style.title} data-testid="noun-icons-submenu-title">
      {isCIM ? (
        nounIconCIM
      ) : (
        <svg
          width="1.25em"
          height="1.25em"
          viewBox="0 0 22 23"
          fill="none"
          stroke="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeMiterlimit="10"
        >
          <path d="M1.4 14.8a1 1 0 0 1 1-1h4.4a1 1 0 0 1 1 1v4.4a1 1 0 0 1-1 1H2.4a1 1 0 0 1-1-1zm12.4-13 6.4 6.4m.001-6.4-6.4 6.4M4.6 8.2a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Zm12.313 6.15a.1.1 0 0 1 .174 0l3.29 5.7a.1.1 0 0 1-.086.15h-6.582a.1.1 0 0 1-.086-.15z" />
        </svg>
      )}
      Icons
    </div>
  );
}

//-----------------------------------------------------------------------------

const debouncedSearchQuery = atom("");

function IconsSearchField() {
  const [debounced, setDebounced] = useAtom(debouncedSearchQuery); // this is debounced
  const [searchValue, setSearchValue] = useState(debounced);

  useDebounce(
    () => {
      setDebounced(searchValue);
      if (searchValue.length > 2) {
        tracking.trackAnalyticsEvent("noun-icon-search", {
          category: consts.TRACKING_CATEGORY.SIDE_MENU,
          searchTerm: searchValue.trim(),
          searchTermLength: searchValue.trim().length,
        });
      }
    },
    500,
    [searchValue]
  );

  return (
    <div className={style.search}>
      <SearchField
        type="search"
        placeholder="Search icons"
        autoFocus={true}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
    </div>
  );
}

//-----------------------------------------------------------------------------
function IconSearchPanel({
  data,
  isCIM,
  error,
  selected,
  onLoadMore,
  totalIconsForQuery,
  isLoading,
  onIconClick,
  onIconInfoClick,
  onTryAfterError,
}: {
  data: NounIconData[];
  isCIM: boolean;
  error: unknown;
  selected?: string;
  onLoadMore: () => void;
  totalIconsForQuery: number;
  isLoading: boolean;
  onIconClick: React.MouseEventHandler<HTMLButtonElement>;
  onIconInfoClick: React.MouseEventHandler<HTMLButtonElement>;
  onTryAfterError: React.MouseEventHandler<HTMLButtonElement>;
}) {
  const setIsDraggingElement = useSetAtom(isDraggingElementAtom);

  function renderIcon(icon: NounIconData) {
    return (
      <div
        className={classNames(style.icon, selected == icon.id && style.selected)}
        style={{ display: "none" }}
        key={icon.id}
      >
        <button onClick={onIconClick} data-id={icon.id}>
          <img
            id={icon.id}
            src={icon.thumbnail_url}
            alt={icon.attribution}
            width="30px"
            height="30px"
            onLoad={(e) => {
              // can show a "loading" indicator..
              e.currentTarget.parentElement!.parentElement!.style.display = "block";
            }}
            onDragStart={(e) => {
              e.preventDefault();
              setIsDraggingElement(true);
              e.currentTarget.parentElement!.click();
              // e.currentTarget.style.opacity = "0.5";
              // e.dataTransfer.effectAllowed = "move";
              // to set drag image that is the real size of the icon we'll place (100x100 px)
              // we have to create a real element, attach it to dom, and the browser takes a
              // screenshot of it.
              // e.dataTransfer.setDragImage(im, 0, 0)
              // e.dataTransfer.setData(
              //   "application/x-canvas-icon",
              //   JSON.stringify({ id: icon.id, icon_url: icon.icon_url })
              // );
            }}
            // onDragEnd={(e) => {
            // e.currentTarget.style.opacity = "1";
            // }}
          />
        </button>
        <button className={style.infoIcon} onClick={onIconInfoClick} data-id={icon.id}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2.077 2.077C3.40689 0.747121 5.2106 0 7.09134 0C8.97208 0 10.7758 0.747121 12.1057 2.077C13.4356 3.40689 14.1827 5.2106 14.1827 7.09134C14.1827 8.97208 13.4356 10.7758 12.1057 12.1057C10.7758 13.4356 8.97208 14.1827 7.09134 14.1827C5.2106 14.1827 3.40689 13.4356 2.077 12.1057C0.747121 10.7758 0 8.97208 0 7.09134C0 5.2106 0.747121 3.40689 2.077 2.077ZM7.09134 1.5C5.60842 1.5 4.18624 2.08909 3.13766 3.13766C2.08909 4.18624 1.5 5.60842 1.5 7.09134C1.5 8.57425 2.08909 9.99643 3.13766 11.045C4.18624 12.0936 5.60842 12.6827 7.09134 12.6827C8.57425 12.6827 9.99643 12.0936 11.045 11.045C12.0936 9.99643 12.6827 8.57425 12.6827 7.09134C12.6827 5.60842 12.0936 4.18624 11.045 3.13766C9.99643 2.08909 8.57425 1.5 7.09134 1.5ZM7.09135 6.34137C7.50556 6.34137 7.84135 6.67715 7.84135 7.09137V10.262C7.84135 10.6762 7.50556 11.012 7.09135 11.012C6.67713 11.012 6.34135 10.6762 6.34135 10.262V7.09137C6.34135 6.67715 6.67713 6.34137 7.09135 6.34137ZM6.63637 3.58435C6.79723 3.47687 6.98634 3.4195 7.1798 3.4195C7.43921 3.4195 7.688 3.52255 7.87144 3.70599C8.05488 3.88943 8.15793 4.13822 8.15793 4.39764C8.15793 4.59109 8.10057 4.7802 7.99308 4.94106C7.8856 5.10191 7.73284 5.22728 7.55411 5.30131C7.37538 5.37535 7.17871 5.39472 6.98897 5.35698C6.79924 5.31924 6.62495 5.22608 6.48815 5.08928C6.35135 4.95248 6.2582 4.7782 6.22046 4.58846C6.18271 4.39872 6.20208 4.20205 6.27612 4.02332C6.35015 3.8446 6.47551 3.69183 6.63637 3.58435Z"
              fill="#657E9A"
            />
          </svg>
        </button>
      </div>
    );
  }

  function renderResults() {
    if (error) {
      return (
        <div>
          <div className={style.errorMessage}>Something went wrong while loading</div>
          <StyledButton
            loading={isLoading}
            title={"Try again"}
            color="#0072FF"
            customStyle={{ borderRadius: "6px", font: "14px Poppins", paddingInline: "3em" }}
            onClick={onTryAfterError}
          />
        </div>
      );
    }
    if (data?.length === 0 && !isLoading && totalIconsForQuery != -1) {
      return <NoSearchResults />;
    }

    const numberMoreIcons = totalIconsForQuery - data.length;
    return (
      <div className={style.iconsGrid}>
        {data?.map(renderIcon)}
        {!isLoading && !error && numberMoreIcons > 0 && (
          <button className={classNames(style.lastRow, style.loadMore)} style={{ height: "40px" }} onClick={onLoadMore}>
            {numberMoreIcons} more icons
          </button>
        )}
        {isLoading && !error && (
          <div className={style.lastRow} style={{ height: "40px" }}>
            <SimpleSpinner />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Title isCIM={isCIM} />
      <IconsSearchField />
      <div className={style.separator}></div>
      <div className={style.results}>{renderResults()}</div>
    </>
  );
}

//-----------------------------------------------------------------------------

function Link({ text, url }: { text: string; url: string }) {
  return (
    <a className={style.link} href={url} rel="noopener noreferrer" target="_blank">
      {text}
    </a>
  );
}

function LicenseLink({ license }: { license?: string }) {
  if (license === "creative-commons-attribution") {
    return <Link text="Creative Commons Attribution (CC BY 3.0)" url="https://creativecommons.org/licenses/by/3.0/" />;
  }
  if (license === "public-domain") {
    return <Link text="Public Domain" url="https://creativecommons.org/publicdomain/mark/1.0/" />;
  }
  if (license === "royalty-free") {
    return <span>Royalty free</span>;
  }
  return <span>unknown</span>;
}

function LicenseInformation({ license }: { license?: string }) {
  return (
    <div className={style.labelAndValue}>
      <span>License: </span>
      <LicenseLink license={license} />
    </div>
  );
}

function CreatorAttribution({ icon, creator }: { icon: string; creator: string }) {
  return (
    <>
      <div className={style.title}>{icon}</div>
      <div className={style.authorInfo}>by {creator}</div>
    </>
  );
}

function IconInfoPanel({
  icon,
  onChooseIcon,
  onBack,
}: {
  icon: NounIconData;
  onChooseIcon: () => void;
  onBack: () => void;
}) {
  const [loadedIcon, setLoadedIcon] = useState(false);
  const setIsDraggingElement = useSetAtom(isDraggingElementAtom);

  useEffect(() => {
    tracking.trackAnalyticsEvent("noun-icon-info-view", {
      category: consts.TRACKING_CATEGORY.SIDE_MENU,
    });
  }, []);

  return (
    <>
      <button onClick={onBack} className={style.back} style={{ position: "absolute", left: "15px", top: "15px" }}>
        ←
      </button>
      <div style={{ display: "grid", placeContent: "center", minHeight: "200px" }}>
        <picture
          style={{
            gridArea: "1/1/-1/-1",
            placeSelf: "center",
          }}
          className={style.draggable}
          onLoad={() => setLoadedIcon(true)}
          draggable={true}
          onDragStart={(e) => {
            e.preventDefault();
            setIsDraggingElement(true);
            onChooseIcon();
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          <source srcSet={icon.icon_url} />
          <img id={icon.id} width="133px" height="133px" src={icon.thumbnail_url} alt={icon.attribution} />
        </picture>
        {!loadedIcon && (
          <div style={{ backgroundColor: "white", gridArea: "1/1/-1/-1", placeSelf: "center" }}>
            <SimpleSpinner />
          </div>
        )}
      </div>
      {/* ---------------------------------- */}
      <div className={style.iconDetailsPane}>
        <CreatorAttribution icon={icon.term} creator={icon.creator.name} />
        <LicenseInformation license={icon.license_description} />
        <StyledButton
          title={"Add to canvas"}
          onClick={onChooseIcon}
          customStyle={{
            borderRadius: "6px",
            font: "normal 14px Poppins",
            paddingInline: "3em",
          }}
        />
      </div>
    </>
  );
}

function NoSearchResults() {
  const query = useAtomValue(debouncedSearchQuery);
  const isCIM = useIsCIMReorderEnabled();
  return (
    <div style={{ display: "grid", placeItems: "center", height: "100%" }}>
      <div>
        {!isCIM && <img src={getPathPrefix("/images/noun-icons-no-results.png")} alt="no results" />}
        <div className={style.errorMessage}>
          No results matching <span className={style.searchTerm}>{`"${query}"`}</span>
        </div>
      </div>
    </div>
  );
}
